import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import {
  ShieldCheck, CheckCircle2, XCircle, AlertTriangle, Info,
  Loader2, RotateCcw, Send, Trash2,
} from 'lucide-react';

const VAL_STATUS_META = {
  passed: { label: 'Pass', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  failed: { label: 'Fail', cls: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: AlertTriangle },
  skipped: { label: 'Skipped', cls: 'bg-muted text-muted-foreground', Icon: Info },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle },
};

const VAL_RULE_LABELS = {
  new_charges_equals_total: 'New charges / totals reconcile',
  duplicate_check: 'Duplicate check',
  auto_assign_property: 'Auto-assign property',
  _parsed_data: 'Parsed data present',
};

const ValStatusBadge = ({ status }) => {
  const m = VAL_STATUS_META[status] || { label: status, cls: 'bg-muted text-muted-foreground', Icon: Info };
  const { Icon } = m;
  return (
    <Badge variant="outline" className={`gap-1 ${m.cls}`} data-testid={`bill-val-status-${status}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </Badge>
  );
};

export const ValidationsTab = ({ billId, accountId }) => {
  const [val, setVal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [acting, setActing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    if (!billId || !accountId) return;
    setLoading(true); setNotFound(false);
    try {
      const { data } = await api.get(`/intake/validation/bill/${billId}`, { params: { account_id: accountId } });
      setVal(data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      setVal(null);
    } finally { setLoading(false); }
  }, [billId, accountId]);

  useEffect(() => { load(); }, [load]);

  const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

  const rerun = async () => {
    setActing(true);
    try {
      await api.post('/intake/validation/run', { bill_id: billId, limit: 1 }, { params: { account_id: accountId } });
      toast.success('Re-validated');
      await load();
    } catch (e) { toast.error(errMsg(e, 'Re-run failed')); }
    finally { setActing(false); }
  };

  const fixSubmit = async () => {
    setActing(true);
    try {
      await api.post(`/intake/validation/${billId}/fix-submit`, {}, { params: { account_id: accountId } });
      toast.success('Bill promoted to All Bills');
      await load();
    } catch (e) { toast.error(errMsg(e, 'Fix & submit failed')); }
    finally { setActing(false); }
  };

  const doDelete = async () => {
    setActing(true);
    try {
      await api.post(`/intake/validation/${billId}/delete`, {}, { params: { account_id: accountId } });
      toast.success('Bill deleted (retained 90 days, then auto-removed)');
      setDeleteOpen(false);
      await load();
    } catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    finally { setActing(false); }
  };

  if (loading) {
    return <div className="p-6 text-center text-xs text-muted-foreground"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading validations…</div>;
  }

  if (notFound || !val) {
    return (
      <div className="p-8 text-center space-y-3" data-testid="bill-validations-empty">
        <p className="text-sm text-muted-foreground">This bill hasn't been validated yet.</p>
        <Button size="sm" onClick={rerun} disabled={acting} data-testid="bill-val-run">
          {acting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
          Run validation
        </Button>
      </div>
    );
  }

  const reasons = val.failure_reasons || [];
  const isDuplicate = reasons.some((r) => r.code === 'duplicate');
  const isDeleted = !!val._deleted;
  const results = (val.results || []).filter((r) => r.rule_key !== '_parsed_data' || r.status === 'failed');

  return (
    <div className="p-4 space-y-4" data-testid="bill-validations-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <ValStatusBadge status={val.overall_status} />
          {val.promoted && (
            <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">Promoted</Badge>
          )}
          {isDeleted && (
            <Badge variant="outline" className="bg-muted text-muted-foreground">Deleted</Badge>
          )}
          {reasons.map((r) => (
            <Badge key={r.code} variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid={`bill-val-reason-${r.code}`}>
              {r.label}
            </Badge>
          ))}
        </div>
        {!isDeleted && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={rerun} disabled={acting} data-testid="bill-val-rerun">
              {acting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
              Re-run
            </Button>
            {!val.promoted && !isDuplicate && (
              <Button size="sm" onClick={fixSubmit} disabled={acting} data-testid="bill-val-fix-submit">
                <Send className="h-3.5 w-3.5 mr-1" /> Fix &amp; submit
              </Button>
            )}
            <Button
              size="sm" variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setDeleteOpen(true)} disabled={acting}
              data-testid="bill-val-delete"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          </div>
        )}
      </div>

      {isDuplicate && !isDeleted && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <Info className="h-4 w-4 mt-0.5" />
          This bill is flagged as a duplicate, so it can't be promoted. Review the duplicate and delete it if confirmed.
        </div>
      )}

      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rule results</div>
        {results.map((r) => (
          <div key={r.rule_key} className="flex items-start gap-3 text-sm border rounded-md p-3">
            <ValStatusBadge status={r.status} />
            <div className="flex-1">
              <div className="font-medium">{VAL_RULE_LABELS[r.rule_key] || r.rule_key}</div>
              {r.status !== 'passed' && r.detail && (
                <pre className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap break-all">
                  {JSON.stringify(r.detail, null, 2)}
                </pre>
              )}
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="bill-val-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this bill?</AlertDialogTitle>
            <AlertDialogDescription>
              The bill will be soft-deleted and retained for 90 days, then automatically removed.
              It will no longer appear in the validation queue or All Bills.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bill-val-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="bill-val-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ValidationsTab;
